import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    IconArrowsHorizontal,
    IconChevronLeft,
    IconChevronRight,
    IconDownload,
    IconMinus,
    IconPlus,
    IconRefresh,
    IconWand,
    IconX,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import Hint from './Hint';

/**
 * Full-screen before/after viewer with a WIPE slider instead of two panes.
 *
 * A sibling of ImagePreviewModal for the image tools: same entry points
 * (`images`, `initialImage`, `onDownloadImage`, prev/next, Esc, adjustments,
 * download) but the two versions sit on top of each other, the "before" clipped
 * to the left of a handle you drag. Judging an edit is easier when both sides
 * are on the same line of the same picture than when your eye jumps between two
 * panes.
 *
 * Zoom and pan work on the picture, never on the divider: the line is anchored
 * to the screen, so at 400% it stays where you left it while the photo moves
 * behind it (the same rule the document-cleanup preview follows).
 *
 * Each item: `{ id, aiImage (after URL), uploadedImage (before URL), fileName?, mimeType? }`.
 */

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const STEP = 0.5;
const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

const CHECKERBOARD = 'repeating-conic-gradient(#d4d4d8 0% 25%, #f4f4f5 0% 50%) 0 0 / 20px 20px';

const DEFAULT_ADJUST = { brightness: 100, contrast: 100, saturation: 100, sharp: 0 };

// `active` re-runs the measurement once the element exists — the modal renders
// nothing while closed, so on mount the ref is still empty.
function useElementSize(ref, active) {
    const [size, setSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
        const el = ref.current;
        if (!active || !el) return undefined;
        const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref, active]);
    return size;
}

/** Redraw a picture with the sidebar's edits burnt in — the screen only shows a CSS filter. */
async function bakeAdjusted(src, mimeType, { brightness, contrast, saturation, sharp }) {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    ctx.drawImage(img, 0, 0);
    if (sharp > 0) {
        const s = sharp / 10;
        const k = [0, -s, 0, -s, 1 + 4 * s, -s, 0, -s, 0];
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const srcPx = new Uint8ClampedArray(imageData.data);
        const dst = imageData.data;
        const w = canvas.width;
        const h = canvas.height;
        for (let y = 1; y < h - 1; y += 1) {
            for (let x = 1; x < w - 1; x += 1) {
                const i = (y * w + x) * 4;
                for (let c = 0; c < 3; c += 1) {
                    dst[i + c] = Math.min(255, Math.max(0,
                        k[1] * srcPx[((y - 1) * w + x) * 4 + c]
                        + k[3] * srcPx[(y * w + (x - 1)) * 4 + c]
                        + k[4] * srcPx[i + c]
                        + k[5] * srcPx[(y * w + (x + 1)) * 4 + c]
                        + k[7] * srcPx[((y + 1) * w + x) * 4 + c]));
                }
            }
        }
        ctx.putImageData(imageData, 0, 0);
    }
    const type = mimeType && /png|webp|jpeg/.test(mimeType) ? mimeType : 'image/png';
    return new Promise((resolve) => canvas.toBlob(resolve, type, 0.95));
}

function ToolbarButton({ label, onClick, disabled, active, children, text }) {
    return (
        <Hint label={label}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-2.5 text-[13px] font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    active ? 'bg-teal-600 hover:bg-teal-700' : 'bg-white/10 hover:bg-white/20'
                }`}
            >
                {children}
                {text ? <span className="hidden sm:inline">{text}</span> : null}
            </button>
        </Hint>
    );
}

export default function ImageCompareSliderModal({
    opened,
    onClose,
    images = [],
    initialImage,
    /** (image, adjustedBlob|null) => void — adjustedBlob is set when the sidebar edits are on. */
    onDownloadImage,
    title,
    /** Header shows the current item's fileName (updates on prev/next). */
    useImageTitle = false,
    hideDownload = false,
    hideAdjustments = false,
    /** Paint a checkerboard behind both versions — for see-through results. */
    checkerboard = false,
}) {
    const { t } = useTranslation();
    const [index, setIndex] = useState(0);
    const [split, setSplit] = useState(50);
    const [view, setView] = useState({ zoom: 1, offset: { x: 0, y: 0 } });
    const [ratio, setRatio] = useState(null);
    const [showAdjust, setShowAdjust] = useState(false);
    const [adjust, setAdjust] = useState(DEFAULT_ADJUST);
    const [downloading, setDownloading] = useState(false);

    const viewportRef = useRef(null);
    const zoomRef = useRef(1);
    const offsetRef = useRef({ x: 0, y: 0 });
    const pointersRef = useRef(new Map());
    const panRef = useRef(null);
    const pinchRef = useRef(null);
    const splitDragRef = useRef(false);

    const current = images[index] || null;
    const port = useElementSize(viewportRef, opened && !!current);

    // ── open / navigate ────────────────────────────────────────────────────────
    const resetView = useCallback(() => {
        zoomRef.current = 1;
        offsetRef.current = { x: 0, y: 0 };
        setView({ zoom: 1, offset: { x: 0, y: 0 } });
    }, []);

    useEffect(() => {
        if (!opened) return;
        const at = initialImage ? images.findIndex((img) => img.id === initialImage.id) : 0;
        setIndex(at >= 0 ? at : 0);
        setSplit(50);
        setRatio(null);
        setShowAdjust(false);
        setAdjust(DEFAULT_ADJUST);
        resetView();
    }, [opened, initialImage]); // eslint-disable-line react-hooks/exhaustive-deps

    const goTo = useCallback((next) => {
        if (next < 0 || next >= images.length) return;
        setIndex(next);
        setSplit(50);
        setRatio(null);
        resetView();
    }, [images.length, resetView]);

    // ── frame geometry ─────────────────────────────────────────────────────────
    // The picture is fitted into the viewport once (fitW × fitH) and then zoomed and
    // panned as a whole; both versions fill that same box, so they stay aligned.
    const fit = useMemo(() => {
        if (!port.width || !port.height) return { width: 0, height: 0 };
        const r = ratio || 1;
        const width = Math.min(port.width, port.height * r);
        return { width, height: width / r };
    }, [port.width, port.height, ratio]);

    const clampOffset = useCallback((zoom, offset) => {
        const maxX = Math.max(0, (fit.width * zoom - port.width) / 2);
        const maxY = Math.max(0, (fit.height * zoom - port.height) / 2);
        return {
            x: Math.min(maxX, Math.max(-maxX, offset.x)),
            y: Math.min(maxY, Math.max(-maxY, offset.y)),
        };
    }, [fit.width, fit.height, port.width, port.height]);

    const applyView = useCallback((zoomValue, offsetValue) => {
        const zoom = clampZoom(zoomValue);
        const offset = zoom === 1 ? { x: 0, y: 0 } : clampOffset(zoom, offsetValue);
        zoomRef.current = zoom;
        offsetRef.current = offset;
        setView({ zoom, offset });
    }, [clampOffset]);

    /** Zoom about a point measured from the viewport centre, keeping that pixel still. */
    const zoomAbout = useCallback((nextZoom, px, py) => {
        const from = zoomRef.current;
        const to = clampZoom(nextZoom);
        if (to === from) return;
        const k = to / from;
        const o = offsetRef.current;
        applyView(to, { x: px - (px - o.x) * k, y: py - (py - o.y) * k });
    }, [applyView]);

    // ── keyboard, scroll lock ─────────────────────────────────────────────────
    useEffect(() => {
        if (!opened) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
            if (e.target?.closest?.('[data-compare-handle]')) return; // handle owns the arrows
            if (e.key === 'ArrowLeft') goTo(index - 1);
            if (e.key === 'ArrowRight') goTo(index + 1);
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKey);
        };
    }, [opened, onClose, goTo, index]);

    // Wheel is bound by hand: React's onWheel is passive and cannot stop the page scrolling.
    useEffect(() => {
        const el = viewportRef.current;
        if (!opened || !el) return undefined;
        const onWheel = (e) => {
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            zoomAbout(
                zoomRef.current * Math.exp(-e.deltaY / 320),
                e.clientX - rect.left - rect.width / 2,
                e.clientY - rect.top - rect.height / 2,
            );
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [opened, zoomAbout, current?.id]);

    // ── pan / pinch ────────────────────────────────────────────────────────────
    const onPointerDown = useCallback((e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointersRef.current.size === 2) {
            const [a, b] = [...pointersRef.current.values()];
            pinchRef.current = { distance: Math.hypot(b.x - a.x, b.y - a.y) || 1, zoom: zoomRef.current };
            panRef.current = null;
            return;
        }
        if (zoomRef.current > 1) {
            panRef.current = { x: e.clientX, y: e.clientY, offset: offsetRef.current };
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        }
    }, []);

    const onPointerMove = useCallback((e) => {
        if (!pointersRef.current.has(e.pointerId)) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const pinch = pinchRef.current;
        if (pinch && pointersRef.current.size === 2) {
            const [a, b] = [...pointersRef.current.values()];
            const distance = Math.hypot(b.x - a.x, b.y - a.y) || 1;
            applyView(pinch.zoom * (distance / pinch.distance), offsetRef.current);
            return;
        }
        const pan = panRef.current;
        if (!pan) return;
        applyView(zoomRef.current, {
            x: pan.offset.x + (e.clientX - pan.x),
            y: pan.offset.y + (e.clientY - pan.y),
        });
    }, [applyView]);

    const endPointer = useCallback((e) => {
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2) pinchRef.current = null;
        if (pointersRef.current.size === 0) panRef.current = null;
    }, []);

    // ── the wipe handle ────────────────────────────────────────────────────────
    // `split` is a percentage of the picture's VISIBLE span on screen (the frame,
    // cropped to the viewport when zoomed), so the handle can never leave the
    // image for the dark margins beside it. Kept in a ref for the drag handler.
    const spanRef = useRef({ left: 0, right: 0 });
    const splitFromX = useCallback((clientX) => {
        const el = viewportRef.current;
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        const { left, right } = spanRef.current;
        if (right - left <= 0) return null;
        return Math.max(0, Math.min(100, ((clientX - rect.left - left) / (right - left)) * 100));
    }, []);

    const onSplitDown = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        splitDragRef.current = true;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }, []);
    const onSplitMove = useCallback((e) => {
        if (!splitDragRef.current) return;
        e.stopPropagation();
        const next = splitFromX(e.clientX);
        if (next != null) setSplit(next);
    }, [splitFromX]);
    const onSplitUp = useCallback((e) => {
        splitDragRef.current = false;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    }, []);

    // ── download ───────────────────────────────────────────────────────────────
    const adjusted = adjust.brightness !== 100 || adjust.contrast !== 100
        || adjust.saturation !== 100 || adjust.sharp !== 0;

    const handleDownload = async () => {
        if (!current) return;
        setDownloading(true);
        try {
            let blob = null;
            if (adjusted) {
                try { blob = await bakeAdjusted(current.aiImage, current.mimeType, adjust); } catch { blob = null; }
            }
            if (onDownloadImage) {
                await onDownloadImage(current, blob);
            } else {
                const url = blob ? URL.createObjectURL(blob) : current.aiImage;
                const link = document.createElement('a');
                link.href = url;
                link.download = current.fileName || 'image';
                document.body.appendChild(link);
                link.click();
                link.remove();
                if (blob) URL.revokeObjectURL(url);
                notifications.show({ title: t('common.success'), message: t('imageGeneration.imageDownloaded'), color: 'green' });
            }
        } catch {
            notifications.show({ title: t('common.error'), message: t('imageGeneration.downloadFailed'), color: 'red' });
        } finally {
            setDownloading(false);
        }
    };

    if (!opened || !current) return null;

    // Divider in viewport pixels, and the same line in the frame's own percentages —
    // which is what clip-path needs, since the "before" layer zooms with the frame.
    const { zoom, offset } = view;
    const frameLeft = port.width / 2 + offset.x - (fit.width * zoom) / 2;
    const frameRight = frameLeft + fit.width * zoom;
    const frameTop = port.height / 2 + offset.y - (fit.height * zoom) / 2;
    const frameBottom = frameTop + fit.height * zoom;
    // The part of the picture actually on screen — the slider lives inside it.
    const spanLeft = Math.max(0, frameLeft);
    const spanRight = Math.min(port.width, frameRight);
    const spanTop = Math.max(0, frameTop);
    const spanBottom = Math.min(port.height, frameBottom);
    spanRef.current = { left: spanLeft, right: spanRight };
    const splitX = spanLeft + ((spanRight - spanLeft) * split) / 100;
    const clipPct = fit.width
        ? Math.max(0, Math.min(100, ((splitX - frameLeft) / (fit.width * zoom)) * 100))
        : split;
    // The আগে / পরে badges sit on the picture's own corners while it fits the
    // screen, and pin to the screen corners once it is zoomed past them.
    const badgeTop = Math.max(12, frameTop + 12);
    const badgeLeft = Math.max(12, frameLeft + 12);
    const badgeRight = Math.max(12, port.width - frameRight + 12);

    const cssFilter = adjusted
        ? `brightness(${adjust.brightness}%) contrast(${adjust.contrast}%) saturate(${adjust.saturation}%)${adjust.sharp > 0 ? ' url(#icsm-sharpen)' : ''}`
        : undefined;
    const hasBefore = !!current.uploadedImage;
    const headerTitle = title || (useImageTitle ? current.fileName : '');
    const s = adjust.sharp / 10;

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={headerTitle || t('documentCleanupImage.compare')}
            // Sized to the SCREEN, not to `inset-0`: when the page body is wider than
            // the viewport (or carries a transform) `inset-0` spans that box instead,
            // pushing the toolbar off the right edge and the slider off-centre.
            style={{ width: '100vw', height: '100dvh' }}
            className="fixed left-0 top-0 z-[99999] flex flex-col overflow-hidden bg-black/85 backdrop-blur-md animate-ipmImageReveal"
        >
            <svg aria-hidden="true" className="absolute h-0 w-0 overflow-hidden">
                <defs>
                    <filter id="icsm-sharpen">
                        <feConvolveMatrix
                            order="3"
                            preserveAlpha="true"
                            kernelMatrix={`0 ${-s} 0 ${-s} ${1 + 4 * s} ${-s} 0 ${-s} 0`}
                        />
                    </filter>
                </defs>
            </svg>

            {/* ── Top bar ── */}
            <div className="relative z-30 flex flex-none items-center gap-2 px-3 py-2.5 sm:px-4">
                <div className="min-w-0 flex-1">
                    {headerTitle ? (
                        <p className="truncate text-[13.5px] font-semibold text-white">{headerTitle}</p>
                    ) : null}
                </div>

                {!hideDownload ? (
                    <Hint label={t('imageGeneration.tooltipDownload')}>
                        <button
                            type="button"
                            onClick={handleDownload}
                            disabled={downloading}
                            aria-label={t('imageGeneration.tooltipDownload')}
                            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-[13px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
                        >
                            {downloading
                                ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                                : <IconDownload size={17} />}
                            <span className="hidden sm:inline">{t('imageGeneration.labelDownload')}</span>
                        </button>
                    </Hint>
                ) : null}

                {!hideAdjustments ? (
                    <ToolbarButton
                        label={t('imageGeneration.tooltipAdjustments')}
                        onClick={() => setShowAdjust((v) => !v)}
                        active={showAdjust}
                        text={t('imageGeneration.labelGlow')}
                    >
                        <IconWand size={17} />
                    </ToolbarButton>
                ) : null}

                <Hint label={t('imageGeneration.tooltipClose')}>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('imageGeneration.tooltipClose')}
                        className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-600 text-white transition-colors hover:bg-red-700"
                    >
                        <IconX size={17} stroke={2.6} />
                    </button>
                </Hint>
            </div>

            {/* ── Body ── */}
            <div className="flex min-h-0 flex-1">
                <div className="group relative min-w-0 flex-1 overflow-hidden">
                    <div
                        ref={viewportRef}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={endPointer}
                        onPointerCancel={endPointer}
                        onDoubleClick={(e) => {
                            if (zoomRef.current > 1) { resetView(); return; }
                            const rect = e.currentTarget.getBoundingClientRect();
                            zoomAbout(2.5, e.clientX - rect.left - rect.width / 2, e.clientY - rect.top - rect.height / 2);
                        }}
                        className={`absolute inset-0 flex touch-none select-none items-center justify-center overflow-hidden ${
                            zoom > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'
                        }`}
                    >
                        <div
                            className="relative flex-none"
                            style={{
                                width: fit.width,
                                height: fit.height,
                                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                                transformOrigin: 'center center',
                                background: checkerboard ? CHECKERBOARD : undefined,
                            }}
                        >
                            {/* After — the base layer. Its natural size decides the frame. */}
                            <img
                                src={current.aiImage}
                                alt={t('imageGeneration.afterLabel')}
                                draggable={false}
                                onLoad={(e) => {
                                    const el = e.currentTarget;
                                    if (el.naturalHeight) setRatio(el.naturalWidth / el.naturalHeight);
                                }}
                                className="pointer-events-none absolute inset-0 h-full w-full object-contain"
                                style={{ filter: cssFilter }}
                            />
                            {/* Before — on top, clipped to the LEFT of the handle. It rides
                                inside the transform: it is the same picture and must zoom
                                and pan with the one under it. */}
                            {hasBefore ? (
                                <img
                                    src={current.uploadedImage}
                                    alt={t('imageGeneration.beforeLabel')}
                                    draggable={false}
                                    className="pointer-events-none absolute inset-0 h-full w-full object-contain"
                                    style={{
                                        clipPath: `inset(0 ${100 - clipPct}% 0 0)`,
                                        background: checkerboard ? CHECKERBOARD : undefined,
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>

                    {/* Divider, handle and labels sit OUTSIDE the zoom transform, in
                        viewport pixels: same size and same place at any zoom. */}
                    {hasBefore ? (
                        <>
                            <div
                                className="pointer-events-none absolute z-10 w-[2px] -translate-x-1/2 bg-primary shadow-[0_0_0_1px_rgba(255,255,255,0.7)]"
                                style={{ left: splitX, top: spanTop, height: Math.max(0, spanBottom - spanTop) }}
                            />
                            <span
                                className="pointer-events-none absolute z-10 rounded-full bg-black/60 px-3 py-1 text-[12px] font-bold text-white transition-opacity"
                                style={{ top: badgeTop, left: badgeLeft, opacity: splitX - badgeLeft > 60 ? 1 : 0 }}
                            >
                                {t('imageGeneration.beforeLabel')}
                            </span>
                            <span
                                className="pointer-events-none absolute z-10 rounded-full bg-black/60 px-3 py-1 text-[12px] font-bold text-white transition-opacity"
                                style={{ top: badgeTop, right: badgeRight, opacity: port.width - badgeRight - splitX > 60 ? 1 : 0 }}
                            >
                                {t('imageGeneration.afterLabel')}
                            </span>
                            <button
                                type="button"
                                data-compare-handle
                                onPointerDown={onSplitDown}
                                onPointerMove={onSplitMove}
                                onPointerUp={onSplitUp}
                                onPointerCancel={onSplitUp}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowLeft') { e.preventDefault(); setSplit((v) => Math.max(0, v - 2)); }
                                    if (e.key === 'ArrowRight') { e.preventDefault(); setSplit((v) => Math.min(100, v + 2)); }
                                }}
                                aria-label={t('documentCleanupImage.compare')}
                                className="absolute z-20 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize touch-none items-center justify-center rounded-full border-2 border-white bg-primary text-white shadow-[0_2px_12px_rgba(0,0,0,0.45)]"
                                style={{ left: splitX, top: (spanTop + spanBottom) / 2 }}
                            >
                                <IconArrowsHorizontal size={20} stroke={2.2} />
                            </button>
                        </>
                    ) : null}

                    {/* Prev / next */}
                    {index > 0 ? (
                        <Hint label={t('imageGeneration.tooltipPrev')} side="right">
                            <button
                                type="button"
                                onClick={() => goTo(index - 1)}
                                aria-label={t('imageGeneration.tooltipPrev')}
                                className="absolute left-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                            >
                                <IconChevronLeft size={26} />
                            </button>
                        </Hint>
                    ) : null}
                    {index < images.length - 1 ? (
                        <Hint label={t('imageGeneration.tooltipNext')} side="left">
                            <button
                                type="button"
                                onClick={() => goTo(index + 1)}
                                aria-label={t('imageGeneration.tooltipNext')}
                                className="absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                            >
                                <IconChevronRight size={26} />
                            </button>
                        </Hint>
                    ) : null}

                    {/* Zoom controls + counter */}
                    <div
                        className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/65 px-2 py-1.5 backdrop-blur-md"
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        <Hint label={t('imageGeneration.tooltipZoomOut')} side="top">
                            <button
                                type="button"
                                onClick={() => zoomAbout(zoomRef.current - STEP, 0, 0)}
                                disabled={zoom <= MIN_ZOOM}
                                aria-label={t('imageGeneration.tooltipZoomOut')}
                                className="flex h-8 w-8 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20 disabled:opacity-40"
                            >
                                <IconMinus size={15} />
                            </button>
                        </Hint>
                        <span className="min-w-[46px] text-center text-[12px] font-bold tabular-nums text-white">
                            {Math.round(zoom * 100)}%
                        </span>
                        <Hint label={t('imageGeneration.tooltipZoomIn')} side="top">
                            <button
                                type="button"
                                onClick={() => zoomAbout(zoomRef.current + STEP, 0, 0)}
                                disabled={zoom >= MAX_ZOOM}
                                aria-label={t('imageGeneration.tooltipZoomIn')}
                                className="flex h-8 w-8 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20 disabled:opacity-40"
                            >
                                <IconPlus size={15} />
                            </button>
                        </Hint>
                        <Hint label={t('imageGeneration.tooltipResetZoom')} side="top">
                            <button
                                type="button"
                                onClick={() => { resetView(); setSplit(50); }}
                                aria-label={t('imageGeneration.tooltipResetZoom')}
                                className="flex h-8 w-8 items-center justify-center rounded-full text-orange-400 transition-colors hover:bg-white/20"
                            >
                                <IconRefresh size={15} />
                            </button>
                        </Hint>
                        {images.length > 1 ? (
                            <span className="ml-1 border-l border-white/20 pl-2.5 pr-1.5 text-[12px] font-semibold tabular-nums text-white/80">
                                {index + 1} / {images.length}
                            </span>
                        ) : null}
                    </div>
                </div>

                {/* ── Adjustments sidebar ── */}
                {showAdjust ? (
                    <aside className="flex w-[260px] flex-none flex-col overflow-y-auto border-l border-white/10 bg-white px-4 py-3 dark:bg-zinc-900">
                        <div className="mb-4 flex min-h-7 items-center justify-between">
                            <span className="text-sm font-bold text-zinc-800 dark:text-zinc-100">{t('imageGeneration.imageAdjustments')}</span>
                            {adjusted ? (
                                <Hint label={t('imageGeneration.resetAdjustments')}>
                                    <button
                                        type="button"
                                        onClick={() => setAdjust(DEFAULT_ADJUST)}
                                        aria-label={t('imageGeneration.resetAdjustments')}
                                        className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                                    >
                                        <IconRefresh size={15} />
                                    </button>
                                </Hint>
                            ) : null}
                        </div>
                        <div className="flex flex-col gap-3.5">
                            {[
                                { key: 'brightness', label: t('imageGeneration.brightness'), min: 50, max: 200, show: (v) => `${v >= 100 ? '+' : ''}${v - 100}%` },
                                { key: 'contrast', label: t('imageGeneration.contrast'), min: 50, max: 200, show: (v) => `${v >= 100 ? '+' : ''}${v - 100}%` },
                                { key: 'saturation', label: t('imageGeneration.saturation'), min: 0, max: 200, show: (v) => `${v >= 100 ? '+' : ''}${v - 100}%` },
                                { key: 'sharp', label: t('imageGeneration.sharp'), min: 0, max: 10, show: (v) => `${v * 10}%` },
                            ].map(({ key, label, min, max, show }) => (
                                <div key={key}>
                                    <div className="mb-1 flex items-center justify-between">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
                                        <span className="text-xs font-bold text-orange-500">{show(adjust[key])}</span>
                                    </div>
                                    <input
                                        type="range"
                                        className="w-full accent-orange-500"
                                        min={min}
                                        max={max}
                                        step={1}
                                        value={adjust[key]}
                                        onChange={(e) => setAdjust((prev) => ({ ...prev, [key]: Number(e.target.value) }))}
                                    />
                                </div>
                            ))}
                        </div>
                    </aside>
                ) : null}
            </div>
        </div>,
        document.body,
    );
}
